const OFFICIAL_DOMAIN_SUFFIXES = ['pudutech.com', 'pudurobotics.com']

const OFFICIAL_EXACT_HOSTS = ['pudu-file-cdn.oss-cn-shenzhen.aliyuncs.com']

function parseHttpUrl(href: unknown): URL | null {
  if (typeof href !== 'string') {
    return null
  }

  const value = href.trim()
  if (!value) {
    return null
  }

  const normalizedValue = value.startsWith('//') ? `https:${value}` : value
  if (!/^https?:\/\//i.test(normalizedValue)) {
    return null
  }

  try {
    return new URL(normalizedValue)
  } catch (_) {
    return null
  }
}

export function isOfficialHost(hostname: string): boolean {
  const normalizedHostname = hostname.trim().toLowerCase()

  if (OFFICIAL_EXACT_HOSTS.includes(normalizedHostname)) {
    return true
  }

  return OFFICIAL_DOMAIN_SUFFIXES.some((domain) => {
    return normalizedHostname === domain || normalizedHostname.endsWith(`.${domain}`)
  })
}

export function isExternalHttpHref(href: unknown): boolean {
  const url = parseHttpUrl(href)
  return !!url && !isOfficialHost(url.hostname)
}

function mergeRelTokens(currentRel: string | null | undefined, tokensToAdd: string[]): string {
  const tokens = new Set(
    (currentRel || '')
      .split(/\s+/)
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
  )

  tokensToAdd.forEach((token) => tokens.add(token))
  return Array.from(tokens).join(' ')
}

export function getLinkRelForHref(
  href: unknown,
  currentRel?: string | null,
  target?: string | null
): string | undefined {
  if (!isExternalHttpHref(href)) {
    return currentRel || undefined
  }

  const requiredRelTokens = ['nofollow']
  if (target?.toLowerCase() === '_blank') {
    requiredRelTokens.push('noopener')
  }

  return mergeRelTokens(currentRel, requiredRelTokens)
}

export function applyExternalLinkRelToDocument(doc: Document): void {
  doc.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
    const rel = getLinkRelForHref(anchor.getAttribute('href'), anchor.getAttribute('rel'), anchor.getAttribute('target'))
    if (rel) {
      anchor.setAttribute('rel', rel)
    }
  })
}

function getAttributeValue(tag: string, attributeName: string): string | null {
  const match = tag.match(new RegExp(`\\s${attributeName}\\s*=\\s*(["'])(.*?)\\1`, 'i'))
  return match?.[2] ?? null
}

function applyExternalLinkRelToAnchorTag(tag: string): string {
  const href = getAttributeValue(tag, 'href')
  const rel = getLinkRelForHref(href, getAttributeValue(tag, 'rel'), getAttributeValue(tag, 'target'))

  if (!rel) {
    return tag
  }

  if (/\srel\s*=\s*(["']).*?\1/i.test(tag)) {
    return tag.replace(/\srel\s*=\s*(["']).*?\1/i, ` rel="${rel}"`)
  }

  return tag.replace(/\s*\/?>$/, (ending) => ` rel="${rel}"${ending}`)
}

export function applyExternalLinkRelToHtml(html: string): string {
  if (!html) {
    return html
  }

  if (typeof DOMParser === 'undefined') {
    return html.replace(/<a\b[^>]*>/gi, applyExternalLinkRelToAnchorTag)
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  applyExternalLinkRelToDocument(doc)
  return doc.body.innerHTML
}
